import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Send, MessageCircle, Sparkles, Loader2, Lock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";

/**
 * Fonico AI chat panel.
 *
 * Renders the conversation, the seed-question chips, an input and a
 * dynamic "N questions remaining" counter next to the chat input. The
 * counter is data-driven from `GET /api/chat/quota` so any plan with a
 * different limit works without a code change:
 *   - `unlimited=true`  → nothing rendered (Pro / Admin)
 *   - `unlimited=false` → "N questions remaining", turns red at 0, and
 *                         the input is locked with an upgrade CTA.
 */
export default function FonicoChat({ analysisId, initialHistory = [] }) {
  const { t } = useTranslation();
  const suggestions = t("chat.suggestions", { returnObjects: true }) || [];
  const [history, setHistory] = useState(initialHistory);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [quota, setQuota] = useState(null); // {unlimited, limit, used, remaining}
  const bottomRef = useRef(null);
  const containerRef = useRef(null);
  // Skip the very first mount and any parent-driven history reload · we
  // only auto-scroll after the USER has actively engaged with the chat.
  // Otherwise, every time `Analysis.jsx` refreshes `doc` (e.g. after a
  // master completes and `refresh()` runs), FonicoChat re-initialises
  // and would `scrollIntoView` the whole page down to the chat, hijacking
  // the parent's scroll-to-mastered-versions logic.
  const didUserInteract = useRef(false);

  const refreshQuota = useCallback(async () => {
    try {
      const r = await api.get("/chat/quota");
      setQuota(r.data);
    } catch {
      /* silently ignore · counter simply won't render */
    }
  }, []);

  useEffect(() => {
    refreshQuota();
  }, [refreshQuota]);

  useEffect(() => {
    if (!didUserInteract.current) return;
    // `block: "nearest"` only scrolls if the bottom is out of view AND
    // constrains movement to the smallest amount needed · never yanks
    // the whole page.
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [history, sending]);

  const remaining = quota && !quota.unlimited ? quota.remaining : null;
  const exhausted = remaining === 0;

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || sending || exhausted) return;
    didUserInteract.current = true;
    setInput("");
    const now = new Date().toISOString();
    setHistory((h) => [...h, { role: "user", content: q, timestamp: now }]);
    setSending(true);
    // Optimistic decrement so the counter reacts before the server round-trip
    // completes. If the request fails we roll back both the history AND the
    // optimistic decrement, so the user isn't charged for a failed message.
    let rolledBack = false;
    setQuota((qv) =>
      qv && !qv.unlimited
        ? { ...qv, used: qv.used + 1, remaining: Math.max(0, qv.remaining - 1) }
        : qv
    );
    try {
      const res = await api.post(`/chat/${analysisId}`, { question: q });
      setHistory(res.data.history);
      // Reconcile with authoritative server value (also catches concurrent
      // decrements from other tabs / devices).
      refreshQuota();
    } catch (err) {
      rolledBack = true;
      toast.error(err?.response?.data?.detail || t("chat.toast_error"));
      setHistory((h) => h.slice(0, -1));
    } finally {
      if (rolledBack) {
        setQuota((qv) =>
          qv && !qv.unlimited
            ? { ...qv, used: Math.max(0, qv.used - 1), remaining: qv.remaining + 1 }
            : qv
        );
      }
      setSending(false);
    }
  };

  return (
    <div className="card-elev p-6 flex flex-col gap-4" data-testid="fonico-chat">
      <div className="flex items-center gap-2">
        <MessageCircle size={18} className="text-[color:var(--brand)]" />
        <h3 className="font-display text-xl font-semibold">{t("chat.title")}</h3>
      </div>
      <p className="text-sm text-[color:var(--fg-secondary)]">{t("chat.subtitle")}</p>

      {history.length === 0 && !exhausted && (
        <div className="flex flex-wrap gap-2">
          {suggestions.map((s) => (
            <button
              key={s}
              className="pill text-xs"
              onClick={() => send(s)}
              disabled={sending || exhausted}
              data-testid="chat-suggestion"
            >
              <Sparkles size={12} className="inline mr-1.5 -mt-0.5" />
              {s}
            </button>
          ))}
        </div>
      )}

      <div className="flex flex-col gap-3 max-h-[420px] overflow-y-auto pr-1">
        {history.map((m, i) => (
          <div
            key={`${m.role}-${i}-${(m.content || "").slice(0, 30)}`}
            className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
            data-testid={`chat-msg-${m.role}`}
          >
            <div
              className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                m.role === "user"
                  ? "bg-[color:var(--brand)] text-white rounded-br-sm"
                  : "bg-white/5 border border-white/10 text-white rounded-bl-sm"
              }`}
            >
              {m.role === "assistant" && (
                <div className="tiny-caps text-[9px] mb-1 opacity-70">{t("chat.ai_label")}</div>
              )}
              <div className="whitespace-pre-wrap">{m.content}</div>
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="bg-white/5 border border-white/10 rounded-2xl rounded-bl-sm px-4 py-3">
              <Loader2 className="animate-spin" size={16} />
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {exhausted && (
        <div
          className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-100 flex items-start gap-2"
          data-testid="chat-exhausted-notice"
          role="status"
        >
          <Lock size={16} className="mt-0.5 shrink-0" />
          <span>{t("chat.exhausted_notice")}</span>
        </div>
      )}

      <form
        className="flex items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          send();
        }}
      >
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={
            exhausted ? t("chat.placeholder_locked") : t("chat.placeholder")
          }
          className="flex-1 bg-white/[0.03] border border-white/10 focus:border-[color:var(--brand)] rounded-lg px-4 py-3 text-sm text-white outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={sending || exhausted}
          data-testid="chat-input"
        />
        <button
          type="submit"
          className="btn-primary flex items-center gap-2 !py-3"
          disabled={sending || exhausted || !input.trim()}
          data-testid="chat-send"
        >
          <Send size={16} />
        </button>
      </form>

      {/* Counter: rendered ONLY when the caller has a bounded plan. Admins
          and unlimited-tier users get no visual noise here. */}
      {quota && !quota.unlimited && (
        <div
          className={`text-xs text-right tabular-nums ${
            exhausted
              ? "text-red-300"
              : remaining <= 1
              ? "text-amber-300"
              : "text-[color:var(--fg-muted)]"
          }`}
          data-testid="chat-quota-counter"
        >
          {t("chat.quota_remaining", {
            count: remaining,
            used: quota.used,
            limit: quota.limit,
          })}
        </div>
      )}
    </div>
  );
}
